/**
 * What a blast does to the hazards and the special kinds. `damage.ts` calls this at the top
 * of every `explode`, before it harms anyone - so a flamethrower caught in the blast is
 * still seen alive here, and armour this destroys is skipped by the ordinary damage loop.
 *
 * It must stay free of `damage.ts` imports (a type is fine): that way there is no cycle.
 */

import { makeNoise } from '../alert';
import { type Ctx, isAlive } from '../ctx';
import type { BlastPower } from '../damage';
import { destroyOutright, isAutomated, tankOf } from '../enemies/specials/kinds';
import { distanceToSpine, hullSpine } from '../enemies/specials/vehicleHull';
import { HAZARDS, SPECIALS, VEHICLES } from '../tuning';
import { within } from './terrain';

export function blastHazards(
  ctx: Ctx,
  x: number,
  z: number,
  radius: number,
  power: BlastPower,
  byPlayer: boolean,
): void {
  const w = ctx.world;

  // Mines inside the blast go up with it - each one a pop the garrison can hear (02 §7: "loud").
  const clear = radius * HAZARDS.mineClearFraction;
  for (const m of w.hazards.mines) {
    if (!m.live || !within(m.x, m.z, x, z, clear)) continue;
    m.live = false;
    ctx.events.push({ type: 'mine', x: m.x, z: m.z });
    makeNoise(ctx, m.x, m.z, HAZARDS.minePopNoise, 'blast');
  }

  for (const e of w.enemies) {
    if (!isAlive(e)) continue;
    if (isAutomated(e.kind)) {
      // Carbine and grenades are useless on armour; a LAW, a cook-off or a bomb right beside it is not.
      // R4: a vehicle is a bigger thing to hit than a minitank, so the charge need not be quite under it.
      // Measured to the hull's SPINE, not its middle (the aim audit, 2026-09-23): a LAW that struck a 9.8 m
      // locomotive square on the nose went off 4.9 m from its middle, out of reach, and the engine drove on.
      const cracked = power !== 'grenade' && (e.vehicle !== undefined
        ? distanceToSpine(hullSpine(e.kind, e.x, e.z, e.facing), x, z) <= VEHICLES.killRadius
        : within(e.x, e.z, x, z, SPECIALS.armourKillRadius));
      if (cracked) destroyOutright(ctx, e, byPlayer);
    } else if (e.kind === 'flamethrower' && within(e.x, e.z, x, z, radius)) {
      // His fuel tank is caught in it: it goes up a moment later (enemies/specials/flame.ts).
      const tank = tankOf(w.hazards, e);
      if (tank.intact && tank.fuseTicks < 0) tank.fuseTicks = SPECIALS.ruptureFuseTicks;
    }
  }
}
